import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="charts"
export default class extends Controller {
  static targets = ["periodFilter", "visibilityChart"];

  connect() {
    this.initVisibilityScoreChart();
    this.setupEventListeners();
    this.initShareOfVoiceChart();

    // Generic multi-series line chart (used on Markets show page)
    try {
      const seriesAttr = this.element.dataset.chartSeriesValue;
      const labelsAttr = this.element.dataset.chartLabelsValue;
      if (seriesAttr && labelsAttr && window.ApexCharts) {
        this.seriesData = JSON.parse(seriesAttr);
        this.labelsData = JSON.parse(labelsAttr);
        this.mode = this.element.dataset.chartModeValue || "absolute";
        this.renderMultiSeriesLineChart(this.seriesData, this.labelsData);
      }
    } catch (e) {
      console.warn("ChartsController: failed to init multi-series chart", e);
    }
  }

  setupEventListeners() {
    // Find all period filter buttons without relying on targets
    const periodFilters = this.visibilityPeriodButtons();

    if (periodFilters.length > 0) {
      periodFilters.forEach((button) => {
        button.addEventListener("click", this.handlePeriodChange.bind(this));
      });
    }

    // Topic and current-questions filters redraw the chart on the same period.
    this.element.querySelectorAll("[data-visibility-filter]").forEach((input) => {
      input.addEventListener("change", () =>
        this.fetchVisibilityData(this.visibilityPeriod || "month", this.visibilityCustomDates)
      );
    });
  }

  handlePeriodChange(event) {
    // Remove active class from all period buttons
    const periodButtons = this.visibilityPeriodButtons();
    periodButtons.forEach((btn) => btn.classList.remove("btn-active"));

    // Add active class to clicked button
    event.target.classList.add("btn-active");

    // Get the period from the button's data attribute
    const period = event.target.dataset.period;

    // Handle custom date range separately
    if (period === "custom") {
      this.showCustomDatePicker();
      return;
    }

    // Fetch data for the selected period (now using the direct period values)
    this.visibilityPeriod = period;
    this.visibilityCustomDates = null;
    this.fetchVisibilityData(period);
  }

  // The share of voice chart has its own period buttons (data-sov-period).
  visibilityPeriodButtons() {
    return this.element.querySelectorAll(".join-item.btn:not([data-sov-period])");
  }

  showCustomDatePicker(
    onApply = (dates) => {
      this.visibilityPeriod = "custom";
      this.visibilityCustomDates = dates;
      this.fetchVisibilityData("custom", dates);
    },
    onCancel = () => this.revertToPreviousActivePeriod()
  ) {
    // Create modal backdrop
    const modal = document.createElement("div");
    modal.className =
      "fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center";

    // Create modal content
    const modalContent = document.createElement("div");
    modalContent.className =
      "bg-base-200 p-6 rounded-lg shadow-lg w-full max-w-md";

    // Create modal header
    const header = document.createElement("div");
    header.className = "flex justify-between items-center mb-4";
    header.innerHTML = `
      <h3 class="text-lg font-bold">Select Date Range</h3>
      <button id="close-date-picker" class="btn btn-sm btn-circle">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    `;

    // Create date inputs
    const dateInputs = document.createElement("div");
    dateInputs.className = "grid grid-cols-2 gap-4 mb-6";

    // Get default date values (last 30 days)
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);

    // Format dates for input
    const formatDate = (date) => {
      return date.toISOString().split("T")[0];
    };

    dateInputs.innerHTML = `
      <div class="form-control">
        <label class="label">
          <span class="label-text">Start Date</span>
        </label>
        <input type="date" id="start-date" class="input input-bordered w-full" value="${formatDate(
          startDate
        )}">
      </div>
      <div class="form-control">
        <label class="label">
          <span class="label-text">End Date</span>
        </label>
        <input type="date" id="end-date" class="input input-bordered w-full" value="${formatDate(
          endDate
        )}">
      </div>
    `;

    // Create buttons
    const buttons = document.createElement("div");
    buttons.className = "flex justify-end gap-2";
    buttons.innerHTML = `
      <button id="cancel-date-range" class="btn btn-ghost">Cancel</button>
      <button id="apply-date-range" class="btn btn-primary">Apply</button>
    `;

    // Build modal
    modalContent.appendChild(header);
    modalContent.appendChild(dateInputs);
    modalContent.appendChild(buttons);
    modal.appendChild(modalContent);

    // Add to DOM
    document.body.appendChild(modal);

    // Handle modal interactions
    document
      .getElementById("close-date-picker")
      .addEventListener("click", () => {
        document.body.removeChild(modal);

        // Revert to previous active button
        onCancel();
      });

    document
      .getElementById("cancel-date-range")
      .addEventListener("click", () => {
        document.body.removeChild(modal);

        // Revert to previous active button
        onCancel();
      });

    document
      .getElementById("apply-date-range")
      .addEventListener("click", () => {
        const startDate = document.getElementById("start-date").value;
        const endDate = document.getElementById("end-date").value;

        if (startDate && endDate) {
          // Close modal
          document.body.removeChild(modal);

          // Fetch data with custom date range
          onApply({
            start_date: startDate,
            end_date: endDate,
          });
        }
      });
  }

  // A cancelled custom range keeps the chart as it was: highlight its period.
  revertToPreviousActivePeriod() {
    const period = this.visibilityPeriod || "month";
    this.visibilityPeriodButtons().forEach((btn) =>
      btn.classList.toggle("btn-active", btn.dataset.period === period)
    );
  }

  fetchVisibilityData(period, customDates = null) {
    // Show loading state
    const chartEl = document.getElementById("visibility-score-chart");
    if (chartEl) {
      chartEl.innerHTML = `
        <div class="flex justify-center items-center h-full">
          <span class="loading loading-spinner loading-lg text-primary"></span>
        </div>
      `;
    }

    // Get the domain slug from meta tag
    const domainSlug = document.querySelector(
      'meta[name="domain-slug"]'
    )?.content;

    if (!domainSlug) {
      console.error("Domain slug not found in meta tags");
      return;
    }

    // Build query parameters
    let queryParams = new URLSearchParams();
    queryParams.append("period", period);
    const selectedProvider = new URLSearchParams(window.location.search).get(
      "provider"
    );
    if (selectedProvider) {
      queryParams.append("provider", selectedProvider);
    }

    const topic = this.element.querySelector('[data-visibility-filter="topic"]')?.value;
    if (topic) queryParams.append("topic", topic);
    if (this.element.querySelector('[data-visibility-filter="active_questions"]')?.checked) {
      queryParams.append("active_questions", "1");
    }

    // Add custom date range if provided
    if (customDates) {
      queryParams.append(
        "start_date",
        customDates.start_date || customDates.startDate
      );
      queryParams.append(
        "end_date",
        customDates.end_date || customDates.endDate
      );
    }

    // Fetch the data
    fetch(`/domains/${domainSlug}/visibility_data?${queryParams.toString()}`)
      .then((response) => response.json())
      .then((data) => {
        this.updateVisibilityChart(data);
      })
      .catch((error) => {
        console.error("Error fetching visibility data:", error);
        // Show error state in chart
        if (chartEl) {
          chartEl.innerHTML = `
            <div class="flex flex-col justify-center items-center h-full">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-12 h-12 text-error mb-2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
              <p class="text-center text-error">Failed to load data</p>
            </div>
          `;
        }
      });
  }

  updateVisibilityChart(data) {
    // Get the chart container
    const chartContainer = document.getElementById("visibility-score-chart");
    if (!chartContainer) return;

    if (window.visibilityChart) {
      try {
        window.visibilityChart.destroy();
      } catch (_) {}
      window.visibilityChart = null;
    }

    chartContainer.dataset.analysisDate = data.analysis_date;

    if (
      Array.isArray(data.series) &&
      Array.isArray(data.labels) &&
      data.series.length > 0 &&
      data.labels.length > 0
    ) {
      try {
        this.renderSeriesLineChartForContainer(
          chartContainer,
          data.series,
          data.labels
        );
        return;
      } catch (error) {
        console.error("Failed to render multi-series visibility chart:", error);
      }
    }

    const timePoints = (data.time_series || []).map((point) => point.date);
    const scoreValues = (data.time_series || []).map((point) => point.score);

    this.renderVisibilityScoreChart(
      timePoints,
      scoreValues,
      data.current_score,
      data.analysis_date
    );
  }

  initVisibilityScoreChart() {
    const el = document.getElementById("visibility-score-chart");
    if (el) {
      this.fetchVisibilityData("month");
    }
  }

  initShareOfVoiceChart() {
    const container = document.getElementById("share-of-voice-chart");
    if (!container) return;

    const buttons = this.element.querySelectorAll("[data-sov-period]");
    const activate = (period) =>
      buttons.forEach((btn) =>
        btn.classList.toggle("btn-active", btn.dataset.sovPeriod === period)
      );

    buttons.forEach((button) => {
      button.addEventListener("click", () => {
        const period = button.dataset.sovPeriod;
        if (period !== "custom") {
          this.sovPeriod = period;
          activate(period);
          this.fetchShareOfVoiceData(period);
          return;
        }

        activate("custom");
        this.showCustomDatePicker(
          (dates) => {
            this.sovPeriod = "custom";
            this.fetchShareOfVoiceData("custom", dates);
          },
          () => activate(this.sovPeriod)
        );
      });
    });

    this.sovPeriod = "month";
    this.fetchShareOfVoiceData("month");
  }

  // Same endpoint conventions as fetchVisibilityData: period, custom dates and
  // the page's provider filter.
  fetchShareOfVoiceData(period, customDates = null) {
    const container = document.getElementById("share-of-voice-chart");
    const domainSlug = document.querySelector('meta[name="domain-slug"]')?.content;
    if (!container || !domainSlug) return;

    container.innerHTML = `
      <div class="flex justify-center items-center h-full">
        <span class="loading loading-spinner loading-lg text-primary"></span>
      </div>
    `;

    const queryParams = new URLSearchParams({ period });
    const selectedProvider = new URLSearchParams(window.location.search).get("provider");
    if (selectedProvider) queryParams.append("provider", selectedProvider);
    if (customDates) {
      queryParams.append("start_date", customDates.start_date);
      queryParams.append("end_date", customDates.end_date);
    }

    fetch(`/domains/${domainSlug}/share_of_voice_data?${queryParams.toString()}`)
      .then((response) => response.json())
      .then((data) => {
        if (!Array.isArray(data.labels) || data.labels.length === 0) {
          if (container._apexChart) {
            container._apexChart.destroy();
            container._apexChart = null;
          }
          container.innerHTML = `
            <div class="flex justify-center items-center h-full p-4 text-sm text-base-content/60 text-center">
              No analysis with a share of voice in this period.
            </div>
          `;
          return;
        }

        // The share on all keywords is the main measure; the others start hidden.
        this.renderSeriesLineChartForContainer(container, data.series, data.labels, {
          keepGaps: true,
          hiddenSeries: data.series.filter((s) => s.key !== "all").map((s) => s.name),
        });
      })
      .catch((error) => {
        console.error("Error fetching share of voice data:", error);
        container.innerHTML = `
          <div class="flex justify-center items-center h-full">
            <p class="text-center text-error">Failed to load data</p>
          </div>
        `;
      });
  }

  renderVisibilityScoreChart(
    dates = [],
    scores = [],
    currentScore = null,
    analysisDate = null
  ) {
    // Debug logging
    console.log("[ApexCharts] Rendering chart with dates:", dates);
    console.log("[ApexCharts] Rendering chart with scores:", scores);

    const chartContainer = document.getElementById("visibility-score-chart");
    if (!chartContainer) return;

    if (chartContainer._apexChart) {
      try {
        chartContainer._apexChart.destroy();
      } catch (_) {}
      chartContainer._apexChart = null;
    }

    chartContainer.innerHTML = ""; // Clear any previous chart

    const chart = new ApexCharts(chartContainer, {
      chart: {
        type: "area",
        height: 200,
        toolbar: { show: false },
        background: "#1e1e2f",
        fontFamily: "inherit",
        zoom: { enabled: false, autoScaleYaxis: false },
        selection: { enabled: false },
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 800,
          animateGradually: { enabled: true, delay: 150 },
          dynamicAnimation: { enabled: true, speed: 350 },
        },
        padding: { left: 20, right: 20, top: 10, bottom: 5 },
      },
      theme: { mode: "dark" },
      xaxis: {
        type: "category",
        categories: dates,
        labels: {
          show: true,
          rotate: 0,
          style: { colors: "#aaaaaa", fontSize: "10px" },
          formatter: (value) => value,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
        tooltip: { enabled: false },
      },
      yaxis: {
        show: true,
        min: 0,
        max: 100,
        labels: {
          style: { colors: "#aaaaaa", fontSize: "10px" },
          formatter: (value) => value.toFixed(0),
        },
        tickAmount: 5,
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      stroke: { curve: "smooth", width: 2 },
      dataLabels: { enabled: false },
      tooltip: {
        enabled: true,
        theme: "dark",
        y: {
          formatter: (value) => (typeof value === 'number' ? `Score: ${value}%` : "No data yet"),
        },
        x: { show: true },
      },
      grid: {
        show: true,
        borderColor: "#2A2A3E",
        strokeDashArray: 5,
        position: "back",
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: { top: 0, right: 0, bottom: 0, left: 20 },
      },
      colors: ["#00ff8f"],
      series: [
        {
          name: "Visibility Score",
          data: scores,
        },
      ],
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.2,
          opacityTo: 0.1,
          stops: [0, 90, 100],
        },
      },
      markers: {
        size: 6,
        colors: ["#00ff8f"],
        strokeColors: "#fff",
        strokeWidth: 2,
      },
      annotations: {},
      states: {
        hover: { filter: { type: "none" } },
      },
    });

    chart.render();
    window.visibilityChart = chart;
  }

  // Helper method to make the current data point blink
  addBlinkingAnimation() {
    // Remove any existing animation style to avoid conflicts
    const existingStyle = document.getElementById("chart-blink-animation");
    if (existingStyle) {
      existingStyle.remove();
    }

    // Create a more robust animation
    const style = document.createElement("style");
    style.id = "chart-blink-animation";
    style.textContent = `
      @keyframes pulse-animation {
        0% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.6; transform: scale(1.2); }
        100% { opacity: 1; transform: scale(1); }
      }
      
      .apexcharts-marker,
      .apexcharts-point-annotations circle {
        animation: pulse-animation 1.5s ease-in-out infinite !important;
        transform-origin: center center;
        transform-box: fill-box;
        fill: #00ff8f !important;  /* Force the fill color */
        r: 6 !important;           /* Ensure radius is large enough */
      }
    `;
    document.head.appendChild(style);

    // Apply animation with a delay to ensure elements are ready
    setTimeout(() => {
      const markers = document.querySelectorAll(
        ".apexcharts-marker, .apexcharts-point-annotations circle"
      );
      if (markers.length === 0) {
        console.warn("No markers found for animation");
      }

      markers.forEach((marker) => {
        // Apply styles directly to the element for maximum compatibility
        marker.style.animation = "pulse-animation 1.5s ease-in-out infinite";
        marker.style.transformOrigin = "center center";
        marker.style.fill = "#00ff8f";
        marker.setAttribute("r", "6"); // Set radius directly

        // Make parent element visible if needed
        const parent = marker.parentElement;
        if (parent) {
          parent.style.opacity = "1";
        }
      });
    }, 500); // Longer delay for better reliability
  }

  // Helper method to create a timeline of dates around the current date
  getDateTimeline(totalPoints, currentIndex) {
    const result = [];
    const today = new Date();

    // Calculate days from today for each point
    for (let i = 0; i < totalPoints; i++) {
      const daysDiff = i - currentIndex; // Negative for past, positive for future
      const date = new Date();
      date.setDate(today.getDate() + daysDiff);
      result.push(
        date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
      );
    }

    return result;
  }

  // Renders a multi-series line chart where series is:
  // [ { name: string, data: [ { x: datetime, y: number }, ... ] }, ... ]
  // and labels is an array of datetimes corresponding to snapshot points.
  renderMultiSeriesLineChart(series = [], labels = []) {
    const container = this.element.querySelector(".chart-root");
    this.renderSeriesLineChartForContainer(container, series, labels);
  }

  // options.keepGaps leaves missing points empty instead of drawing them at 0;
  // options.hiddenSeries names the series to hide until picked in the legend.
  renderSeriesLineChartForContainer(container, series = [], labels = [], options = {}) {
    if (!container || !Array.isArray(labels) || labels.length === 0) return;

    // Normalize labels to ISO strings and pretty axis labels
    const labelDates = labels.map((l) => new Date(l));
    const dayKeys = labelDates.map((d) => d.toISOString().slice(0, 10));
    const hasSameDaySnapshots = new Set(dayKeys).size < dayKeys.length;
    const categories = labelDates.map((d) => {
      const dayLabel = d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
      if (!hasSameDaySnapshots) return dayLabel;

      const timeLabel = d.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
      });
      return `${dayLabel} ${timeLabel}`;
    });

    // Normalize series to align with labels (fill missing with 0, or leave a gap)
    const missing = options.keepGaps ? null : 0;
    const normalizedSeries = series.map((s) => {
      const map = new Map(
        (s.data || []).map((p) => [
          new Date(p.x).toISOString(),
          p.y === null || p.y === undefined ? missing : Number(p.y) || 0,
        ])
      );
      const data = labelDates.map((d) => map.get(d.toISOString()) ?? missing);
      return { name: s.name, data };
    });

    // The Y axis fits the lines on show, so hiding brands in the legend zooms
    // in on the others; it is refitted each time the legend changes them.
    const hidden = new Set(options.hiddenSeries || []);
    const fitYAxis = (isHidden) =>
      this.niceYAxis(
        normalizedSeries.filter((s, i) => !isHidden(s, i)).flatMap((s) => s.data)
      );
    const yAxisFor = (bounds) => ({
      ...bounds,
      // One decimal, so a zoomed axis below a few % keeps distinct ticks.
      labels: { formatter: (v) => `${Number(v.toFixed(1))}` },
    });

    // Destroy any previous chart instance attached to this element
    if (container._apexChart) {
      try {
        container._apexChart.destroy();
      } catch (_) {}
      container._apexChart = null;
    }

    // Remove loading placeholder or stale content before mounting ApexCharts.
    container.innerHTML = "";

    const chartOptions = {
      chart: {
        type: "line",
        height: 280,
        toolbar: { show: false },
        background: "transparent",
        fontFamily: "inherit",
        animations: { enabled: true },
        events: {
          updated: (chartContext, { config, globals }) => {
            const collapsed = globals.collapsedSeriesIndices;
            const bounds = fitYAxis((s, i) => collapsed.includes(i));
            const current = config.yaxis[0];
            if (current.min === bounds.min && current.max === bounds.max) return;
            chartContext.updateOptions({ yaxis: yAxisFor(bounds) }, false, false);
          },
        },
      },
      theme: {
        mode: document.documentElement.classList.contains("dark")
          ? "dark"
          : "light",
      },
      xaxis: {
        categories,
        labels: { style: { fontSize: "10px" } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: yAxisFor(fitYAxis((s) => hidden.has(s.name))),
      stroke: { curve: "smooth", width: 2 },
      // A single analysis draws no line: mark the point so it shows.
      markers: { size: categories.length === 1 ? 5 : 0 },
      dataLabels: { enabled: false },
      grid: {
        borderColor: "#e2e8f0",
        strokeDashArray: 4,
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: { formatter: (v) => (v === null || v === undefined ? "—" : `${v}%`) },
      },
      legend: {
        position: "top",
        horizontalAlign: "left",
      },
      series: normalizedSeries,
    };

    const chart = new ApexCharts(container, chartOptions);
    chart.render().then(() => {
      (options.hiddenSeries || []).forEach((name) => chart.hideSeries(name));
    });
    container._apexChart = chart;
  }

  // Y axis bounds around the given values, on round steps (1, 2, 2.5 or 5
  // times a power of ten) so the ticks read cleanly, within 0–100 %.
  niceYAxis(values) {
    const shown = values.filter((v) => v !== null && v !== undefined);
    if (shown.length === 0) return { min: 0, max: 1, tickAmount: 5 };

    let low = Math.min(...shown);
    let high = Math.max(...shown);
    const pad = Math.max((high - low) * 0.1, high * 0.05, 0.1);
    low = Math.max(0, low - pad);
    high = Math.min(100, high + pad);

    // At least 0.1 apart, the precision the tick labels show.
    const rough = Math.max((high - low) / 5, 0.1);
    const magnitude = 10 ** Math.floor(Math.log10(rough));
    const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= rough);
    const min = Math.floor(low / step) * step;
    const max = Math.ceil(high / step) * step;
    return { min, max, tickAmount: Math.round((max - min) / step) };
  }

  // (mode toggle removed)
}
